//@flow
import * as React from 'react';
import Tether from 'tether';
import {
    Box,
    Columns,
    Column,
    Text,
    Stack,
    Divider,
    Flex,
    Button as NeoButton,
    Tooltip,
    Menu,
    MenuButton,
    MenuItem,
    MenuList
} from '@locus-taxy/neo-ui';
import { type TFunction } from 'react-i18next';

import Units from 'common/enums/UnitEnum';
import { neoUIColors } from 'common/enums/ColorEnum';
import { enhanceWithClickOutside } from 'common/components/core/util/HandleOutsideClick';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum,
    popOverFactory
} from 'common/components/core/popover/popOverFactory';

import { PjpPlanStatus } from 'common/models/internal/PermanentJourneyPlan';
import Statistic from 'common/components/core/stats/Statistic';
import FpPlanDetailsTooltip from './FpPlanDetailsTooltip';
import FpPlanEditTooltip from './FpPlanEditTooltip';
import StatisticWithUnit from 'common/components/core/stats/StatisticWithUnit';

import theme from './FpDetailsHeader.scss';
import { type FpPjPlan } from 'common/models/internal/FpPjPlan';
import { type FpSalesbeatPlan } from 'common/models/flow/FpSalesbeatPlan';
import pjpPlansTestIds from 'common/tests/masterManagement/pjpPlans.json';
import variables from 'common/components/core/variables.scss';
import {
    PermissionMatch,
    AccessDeniedMessage,
    RoleOperationEnum
} from 'common/modules/rbac';
import { PlanEditContext } from './planEdits/PlanEditContext';

const NeoButtonWithPopOver = popOverFactory({ Component: NeoButton });

const EnhancedPlanDetailsTooltipContent = enhanceWithClickOutside(
    FpPlanDetailsTooltip
);

const EnhancedPlanEditsTooltipContent = enhanceWithClickOutside(
    FpPlanEditTooltip
);

type State = {
    planFrozen: boolean,
    showPlanInformationContent: boolean,
    isPlanEditTooltipVisible: boolean,
    isPlanEllipsisTooltipVisible: boolean,
    freezeInProgress: boolean
};

type Props = {
    plan: FpPjPlan,
    clientId: string,
    pjpId: string,
    freezePlan: () => void,
    showExportDialog: () => void,
    showEditsDialog: () => void,
    reloadData: () => void,
    salesbeatPlan: ?FpSalesbeatPlan,
    t: TFunction
};

class FpDetailsHeader extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            planFrozen: props.plan.planStatus === PjpPlanStatus.Frozen,
            showPlanInformationContent: false,
            isPlanEditTooltipVisible: false,
            isPlanEllipsisTooltipVisible: false,
            freezeInProgress: false
        };
    }

    static contextType = PlanEditContext;

    showPlanInformation = () => {
        this.setState({ showPlanInformationContent: true }, () => {
            Tether.position();
        });
    };

    hidePlanInformation = () => {
        this.setState({ showPlanInformationContent: false });
    };

    handleOutsideClickPlanInfo = () => {
        this.setState({ showPlanInformationContent: false });
    };

    renderMetric = () => {
        const {
            plan,
            theme,
            t,
            salesbeatPlan,
            metricsBeforeEdit,
            UnitConverter
        } = this.props;
        const { edits } = this.context;
        const {
            availableOutletCount,
            availableSalesmenCount
        } = plan.pjPlanInsights.planDetails.currentPlan;
        const {
            usedSalesmenCount,
            visitCount,
            assignedVisitCount
        } = plan.salesbeatPlan.summary;
        const totalOutlets = plan.outletsInfo.outletIds.length;
        const formattedDistanceBeforeEdit = UnitConverter.convertMeters({
            value: metricsBeforeEdit?.totalTravelDistance,
            pluralizeUnit: true,
            roundOff: true,
            decimals: 2,
            showUnitAbbr: false,
            padLeft: true
        }).valueString;

        const formattedTravelTimeBeforeEdit = UnitConverter.convertSeconds({
            roundOff: true,
            value: metricsBeforeEdit?.totalTravelDuration
        }).valueString;
        return (
            <Column width="fluid">
                <Columns space="medium">
                    <Statistic
                        theme={theme}
                        value={usedSalesmenCount}
                        totalValue={availableSalesmenCount}
                        title={
                            edits?.length
                                ? `${t('fpPlanView.original')}: ${
                                      metricsBeforeEdit?.totalRiders
                                  }`
                                : t('fpPlanView.ridersUsed')
                        }
                        tooltip={t('fpPlanView.ridersUsed')}
                        disablePointerEvents
                        uppercase
                    />
                    <Statistic
                        theme={theme}
                        value={availableOutletCount}
                        totalValue={totalOutlets}
                        tooltip={t('fpPlanView.outletsServed')}
                        title={
                            edits?.length
                                ? `${t('fpPlanView.original')}: ${
                                      metricsBeforeEdit?.availableOutletCount
                                  }`
                                : t('fpPlanView.outletsServed')
                        }
                        disablePointerEvents
                        uppercase
                    />
                    {salesbeatPlan ? (
                        <Statistic
                            theme={theme}
                            value={assignedVisitCount}
                            totalValue={visitCount}
                            title={
                                edits?.length
                                    ? `${t('fpPlanView.original')}: ${
                                          metricsBeforeEdit?.assignedVisitCount
                                      }`
                                    : t('fpPlanView.visitsAllocated')
                            }
                            tooltip={t('fpPlanView.visitsAllocated')}
                            disablePointerEvents
                            uppercase
                        />
                    ) : null}

                    {salesbeatPlan ? (
                        <Statistic
                            theme={theme}
                            value={salesbeatPlan.summary.beatCount}
                            title={
                                edits?.length
                                    ? `${t('fpPlanView.original')}: ${
                                          metricsBeforeEdit?.beatCount
                                      }`
                                    : t('fpPlanView.beatsPlanned')
                            }
                            tooltip={t('fpPlanView.beatsPlanned')}
                            disablePointerEvents
                            uppercase
                        />
                    ) : null}

                    {salesbeatPlan ? (
                        <StatisticWithUnit
                            disablePointerEvents
                            uppercase
                            title={
                                edits?.length
                                    ? `${t(
                                          'fpPlanView.original'
                                      )}: ${formattedDistanceBeforeEdit}`
                                    : t('fpPlanView.totalTravelDistance')
                            }
                            value={salesbeatPlan.summary.totalTravelDistance}
                            sourceUnit={Units.list.length.Meter}
                            padLeft
                            tooltip={t('fpPlanView.totalTravelDistance')}
                            theme={theme}
                        />
                    ) : null}

                    {salesbeatPlan ? (
                        <StatisticWithUnit
                            uppercase
                            title={
                                edits?.length
                                    ? `${t(
                                          'fpPlanView.original'
                                      )}: ${formattedTravelTimeBeforeEdit}`
                                    : t('fpPlanView.totalTravelTime')
                            }
                            theme={theme}
                            value={salesbeatPlan.summary.totalTravelDuration}
                            sourceUnit={Units.list.time.Second}
                            tooltip={t('fpPlanView.totalTravelTime')}
                            disablePointerEvents
                        />
                    ) : null}

                    {salesbeatPlan ? (
                        <StatisticWithUnit
                            uppercase
                            title={
                                edits?.length
                                    ? `${t('fpPlanView.original')}: ${
                                          metricsBeforeEdit?.totalVolume
                                      }`
                                    : t('fpPlanView.totalVolume')
                            }
                            unit={t('fpPlanView.units')}
                            theme={theme}
                            value={salesbeatPlan.summary.totalVolume}
                            tooltip={t('fpPlanView.totalVolume')}
                            disablePointerEvents
                        />
                    ) : null}
                </Columns>
            </Column>
        );
    };

    _freezePlan = () => {
        const { clientId, pjpId, freezePlan } = this.props;
        try {
            this.setState({
                freezeInProgress: true
            });
            freezePlan(clientId, pjpId).then(() => {
                this.setState({
                    freezeInProgress: false,
                    planFrozen: true
                });
            });
        } catch (error) {
            this.setState({
                freezeInProgress: false
            });
        }
    };

    togglePlanEditsPopover = () => {
        const { isPlanEditTooltipVisible } = this.state;
        this.setState({
            isPlanEditTooltipVisible: !isPlanEditTooltipVisible
        });
    };

    closePlanEditsPopover = () => {
        this.setState({
            isPlanEditTooltipVisible: false
        });
    };

    togglePlanEllipsisPopover = () => {
        const { isPlanEllipsisTooltipVisible } = this.state;
        this.setState({
            isPlanEllipsisTooltipVisible: !isPlanEllipsisTooltipVisible
        });
    };

    closeFpEllipsisPopover = () => {
        this.setState({
            isPlanEllipsisTooltipVisible: false
        });
    };

    openFpEllipsisPopover = () => {
        this.setState({
            isPlanEllipsisTooltipVisible: true
        });
    };

    openPlanEditsPopover = () => {
        this.setState({
            isPlanEditTooltipVisible: true
        });
    };

    closePlanEditsPopover = () => {
        this.setState({
            isPlanEditTooltipVisible: false
        });
    };

    showEditsDialog = () => {
        const { showEditsDialog } = this.props;
        this.setState({
            isPlanEditTooltipVisible: false
        });
        showEditsDialog();
    };

    renderFreezeButton: (
        hasPermission: boolean
    ) => React.Node = hasPermission => {
        const { planFrozen, freezeInProgress } = this.state;
        const { t } = this.props;
        return (
            <Tooltip
                shouldWrapChildren={!planFrozen}
                tooltip={
                    !hasPermission ? (
                        <AccessDeniedMessage
                            action={t('fpPlanView.freezePlan')}
                        />
                    ) : null
                }
            >
                <NeoButton
                    disabled={!hasPermission}
                    size="medium"
                    appearance="solid"
                    palette="primary"
                    icon="lock"
                    onClick={this._freezePlan}
                    isLoading={freezeInProgress}
                >
                    {t('fpPlanView.freezePlan')}
                </NeoButton>
            </Tooltip>
        );
    };

    _getExportMenuItem = hasPermission => {
        const { showExportDialog, t } = this.props;
        return (
            <Tooltip
                shouldWrapChildren
                tooltip={
                    !hasPermission ? (
                        <AccessDeniedMessage action={t('fpPlanView.export')} />
                    ) : null
                }
            >
                <MenuItem
                    onClick={showExportDialog}
                    disabled={!hasPermission}
                    data-test-id={pjpPlansTestIds.insights.exportButton}
                >
                    {t('fpPlanView.export')}
                </MenuItem>
            </Tooltip>
        );
    };

    render() {
        const { plan, salesbeatPlan, t } = this.props;
        const { isPlanEditTooltipVisible, planFrozen } = this.state;
        return (
            <Flex
                height={variables.appbarHeight}
                p={2}
                justifyContent="center"
                flexDirection="column"
            >
                <Columns alignY="center" space="medium">
                    <Column width="content">
                        <Stack>
                            <Text
                                fontSize={3}
                                fontWeight={700}
                                data-test-id={pjpPlansTestIds.details.planId}
                            >
                                {plan.id}
                            </Text>
                        </Stack>
                    </Column>
                    <Column width="content">
                        <Box height="inherit">
                            <Columns
                                align="center"
                                alignY="center"
                                height="fluid"
                            >
                                <NeoButtonWithPopOver
                                    appearance="icon"
                                    popOverContent={
                                        <EnhancedPlanDetailsTooltipContent
                                            t={t}
                                            theme={theme}
                                            plan={plan}
                                        />
                                    }
                                    triggerPopoverOn={PopoverTriggerEnum.Click}
                                    icon="information"
                                    color={neoUIColors.grey600.code}
                                    preferPlace={PopoverPlaceEnum.Below}
                                />
                                {salesbeatPlan ? (
                                    <NeoButtonWithPopOver
                                        appearance="icon"
                                        onClick={this.togglePlanEditsPopover}
                                        popOverContent={
                                            <EnhancedPlanEditsTooltipContent
                                                t={t}
                                                handleClickOutside={
                                                    this.closePlanEditsPopover
                                                }
                                                salesbeatPlan={salesbeatPlan}
                                                showEditsDialog={
                                                    this.showEditsDialog
                                                }
                                                theme={theme}
                                            />
                                        }
                                        color={neoUIColors.grey600.code}
                                        isPopOverOpen={isPlanEditTooltipVisible}
                                        icon="dispatcher_plan_edited"
                                        preferPlace={PopoverPlaceEnum.Below}
                                    />
                                ) : null}
                            </Columns>
                        </Box>
                    </Column>

                    <Divider
                        orientation="vertical"
                        color={neoUIColors.grey600.code}
                    />
                    {this.renderMetric()}
                    <Column width="content">
                        <Columns alignY="center" space="medium">
                            <Menu palette="light">
                                <MenuButton
                                    appearance="icon"
                                    icon="overflow_vertical"
                                    size="large"
                                    data-test-id={
                                        pjpPlansTestIds.insights.overflowButton
                                    }
                                />
                                <MenuList>
                                    <PermissionMatch
                                        operations={
                                            RoleOperationEnum.EXPORT_PJP
                                        }
                                        render={this._getExportMenuItem}
                                    />
                                </MenuList>
                            </Menu>

                            {planFrozen ? (
                                <Box
                                    backgroundColor="red.500"
                                    p="6px 13px"
                                    sx={{ cursor: 'not-allowed' }}
                                >
                                    <Text
                                        textTransform="uppercase"
                                        fontSize={2}
                                        fontWeight="bold"
                                        color="white"
                                        sx={{
                                            userSelect: 'none'
                                        }}
                                    >
                                        {t('fpPlanView.frozen')}
                                    </Text>
                                </Box>
                            ) : (
                                <PermissionMatch
                                    operations={RoleOperationEnum.EDIT_PJP}
                                    render={this.renderFreezeButton}
                                />
                            )}
                        </Columns>
                    </Column>
                </Columns>
            </Flex>
        );
    }
}

export default FpDetailsHeader;
